import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { XIcon, CheckCircle2Icon, CheckIcon, LockIcon, CreditCardIcon, SparklesIcon, ArrowLeftIcon, Loader2Icon, TagIcon, AlertCircleIcon } from 'lucide-react';
// Demo referral codes → discount percentage off the monthly price.
const REFERRAL_CODES: Record<string, number> = {
  TALAB10: 10,
  WELCOME20: 20,
  EASYLEASE15: 15
};
type ReferralStatus = 'idle' | 'checking' | 'valid' | 'invalid';
import { useBusiness } from './BusinessContext';
type Step = 'choose' | 'pay' | 'success';
interface UpgradePlanModalProps {
  onClose: () => void;
  initialPlan?: string;
}
export function UpgradePlanModal({
  onClose,
  initialPlan
}: UpgradePlanModalProps) {
  const {
    trialPlan,
    subscribePackage,
    PACKAGES,
    currentPlanId
  } = useBusiness();
  const [step, setStep] = useState<Step>('choose');
  const [selected, setSelected] = useState<string>(initialPlan ?? trialPlan ?? 'Growth');
  const [processing, setProcessing] = useState(false);
  const [referral, setReferral] = useState('');
  const [referralStatus, setReferralStatus] = useState<ReferralStatus>('idle');
  const [discountPct, setDiscountPct] = useState(0);
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  const plan = PACKAGES.find(p => p.id === selected) || paidPackages[0];
  const discountAmount = Math.round(plan.price * discountPct / 100);
  const total = plan.price - discountAmount;
  const applyReferral = () => {
    const code = referral.trim().toUpperCase();
    if (!code) return;
    setReferralStatus('checking');
    setTimeout(() => {
      const pct = REFERRAL_CODES[code];
      if (pct) {
        setDiscountPct(pct);
        setReferralStatus('valid');
      } else {
        setDiscountPct(0);
        setReferralStatus('invalid');
      }
    }, 900);
  };
  const pay = () => {
    setProcessing(true);
    setTimeout(() => {
      setProcessing(false);
      subscribePackage(selected);
      setStep('success');
    }, 1600);
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-4841" data-mp="33p8ikcf9ixp1">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} data-id="element-4842" data-mp="2yyiagrfgcgqn" />
      <motion.div initial={{
      opacity: 0,
      scale: 0.96,
      y: 20
    }} animate={{
      opacity: 1,
      scale: 1,
      y: 0
    }} className="relative w-full max-w-3xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]" data-id="element-4843" data-mp="2uvvimqis6vtg">
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4 border-b border-slate-100 flex items-center justify-between" data-id="element-4844" data-mp="3ik4vh2y0o98g">
          <div className="flex items-center gap-2" data-id="element-4845" data-mp="3dozaq7gcp0ly">
            {step === 'pay' && <button onClick={() => setStep('choose')} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg" data-id="element-4846" data-mp="38q5yeicsvfnd">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-4847" data-mp="1x7ldw3rm1tyn" />
              </button>}
            <div data-id="element-4848" data-mp="1n6j3wsm07471">
              <h2 className="text-lg font-display font-bold text-dark-navy" data-id="element-4849" data-mp="1n2v8tp0wu9q7">
                {step === 'choose' && 'Choose your plan'}
                {step === 'pay' && 'Payment details'}
                {step === 'success' && 'Subscription active'}
              </h2>
              <p className="text-sm text-slate-500" data-id="element-4850" data-mp="21unm7vh59dpa">
                {step === 'choose' && 'Upgrade your Business Hub anytime.'}
                {step === 'pay' && `You're upgrading to the ${selected} plan.`}
                {step === 'success' && 'Thank you for upgrading.'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-4851" data-mp="2frkb3rdpcl3j">
            <XIcon className="w-5 h-5" data-id="element-4852" data-mp="lgxe8dmwm5ad" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-4853" data-mp="qfi5voo3bgn7">
          {step === 'choose' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4854" data-mp="2gurir7ybszgn">
              {paidPackages.map(p => {
            const isSel = selected === p.id;
            const isCurrent = currentPlanId === p.id;
            return <button key={p.id} onClick={() => setSelected(p.id)} className={`text-left rounded-2xl p-5 border-2 transition-colors relative flex flex-col ${isSel ? 'border-primary-indigo bg-indigo-50/50' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-4855" data-mp="2kxec5danasfy">
                    {p.popular && <span className="absolute -top-2.5 left-5 px-2 py-0.5 bg-ai-purple text-white text-[10px] font-bold rounded-full uppercase tracking-wide" data-id="element-4856" data-mp="26zpsg8sbdrch">
                        Popular
                      </span>}
                    {isSel && <CheckCircle2Icon className="w-5 h-5 text-primary-indigo absolute top-4 right-4" data-id="element-4857" data-mp="19hh9km9vwa09" />}
                    <div className="font-bold text-dark-navy mb-1" data-id="element-4858" data-mp="3l3hv38efk63p">
                      {p.name}
                    </div>
                    <div className="text-primary-indigo font-display font-bold text-2xl mb-1" data-id="element-4859" data-mp="3c565n9pavecu">
                      AED {p.price.toLocaleString()}
                      <span className="text-sm text-slate-500 font-normal" data-id="element-4860" data-mp="3h4irbr4fqfdu">
                        /mo
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mb-4" data-id="element-4861" data-mp="cqavhjsvqmhj">
                      {p.listingLimit === Infinity ? '100+ listings' : `${p.listingLimit} listings`}
                    </div>
                    <div className="space-y-1.5 mt-auto" data-id="element-4862" data-mp="3njfr7sz14k3">
                      {p.features.map(f => <div key={f} className="flex items-start gap-1.5 text-xs text-slate-600" data-id="element-4863" data-mp="2jexjz7vk2bti">
                          <CheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-4864" data-mp="24c5yyh2q78dt" />{' '}
                          {f}
                        </div>)}
                    </div>
                    {isCurrent && <div className="mt-4 text-center text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-4865" data-mp="1vikkkyuh3t4x">
                        Current Plan
                      </div>}
                  </button>;
          })}
            </div>}

          {step === 'pay' && <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-4866" data-mp="1clj9qxe8myt2">
              {/* Card form */}
              <div className="md:col-span-3 space-y-4" data-id="element-4867" data-mp="17nhrgeomz3in">
                <Field label="Cardholder name" placeholder="Fadi Al Mansoori" data-id="element-4868" data-mp="17p19a9pxxmmj" />
                <div data-id="element-4869" data-mp="36zk8li9sflt5">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4870" data-mp="36xzn0i5gbd7f">
                    Card number
                  </label>
                  <div className="relative" data-id="element-4871" data-mp="39l0a8e1ceift">
                    <input placeholder="1234 5678 9012 3456" className="w-full pl-4 pr-11 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-4872" data-mp="39k8ijeav94ut" />
                    <CreditCardIcon className="w-5 h-5 text-slate-300 absolute right-3 top-1/2 -translate-y-1/2" data-id="element-4873" data-mp="3o9o7qpur128e" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-4874" data-mp="2qnz2wmkbg68p">
                  <Field label="Expiry" placeholder="MM / YY" data-id="element-4875" data-mp="2vlik9vcb8z4v" />
                  <Field label="CVC" placeholder="123" data-id="element-4876" data-mp="30jan3kehu5nu" />
                </div>
                <Field label="Billing country" placeholder="United Arab Emirates" data-id="element-4877" data-mp="34lfd895qgk5q" />
              </div>

              {/* Summary */}
              <div className="md:col-span-2" data-id="element-4878" data-mp="fvnzfuqoij1g">
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5" data-id="element-4879" data-mp="juevrznblwni">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3" data-id="element-4880" data-mp="15w72pw369j1p">
                    Order Summary
                  </div>
                  <div className="flex items-center justify-between mb-2" data-id="element-4881" data-mp="10vj0ee9htfs6">
                    <span className="text-sm text-slate-600" data-id="element-4882" data-mp="1fnbd0ihwkfq7">
                      {plan.name} plan
                    </span>
                    <span className="font-bold text-dark-navy" data-id="element-4883" data-mp="1atqhcx3s7xu6">
                      AED {plan.price.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mb-3 text-sm text-slate-500" data-id="element-4884" data-mp="1jnbvqfc1bd32">
                    <span data-id="element-4885" data-mp="1yk2segubqaoz">Billed monthly</span>
                  </div>

                  {referralStatus === 'valid' && <div className="flex items-center justify-between mb-2 text-sm" data-id="element-4886" data-mp="1tk82ogmabr5i">
                      <span className="text-green-600 font-medium" data-id="element-4887" data-mp="47msryw19ut3">
                        Referral ({discountPct}% off)
                      </span>
                      <span className="font-bold text-green-600" data-id="element-4888" data-mp="2av3mi3zb1kw9">
                        − AED {discountAmount.toLocaleString()}
                      </span>
                    </div>}

                  {/* Referral code */}
                  <div className="mb-3" data-id="element-4889" data-mp="215ajhrd8ae3m">
                    <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-4890" data-mp="264n8oirknx9d">
                      Referral code
                    </label>
                    <div className="flex gap-2" data-id="element-4891" data-mp="1s3kl21kxiewc">
                      <div className="relative flex-1" data-id="element-4892" data-mp="1w6h9i8x5h587">
                        <TagIcon className="w-4 h-4 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4893" data-mp="1w8223szwme3f" />
                        <input value={referral} onChange={e => {
                      setReferral(e.target.value);
                      if (referralStatus !== 'idle') {
                        setReferralStatus('idle');
                        setDiscountPct(0);
                      }
                    }} onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyReferral();
                      }
                    }} placeholder="Enter code" className={`w-full pl-9 pr-3 py-2 bg-white border rounded-lg text-sm uppercase placeholder:normal-case focus:ring-2 focus:ring-primary-indigo/20 ${referralStatus === 'valid' ? 'border-green-400' : referralStatus === 'invalid' ? 'border-red-400' : 'border-slate-200'}`} data-id="element-4894" data-mp="1i92fsrzv6baf" />
                      </div>
                      <button onClick={applyReferral} disabled={!referral.trim() || referralStatus === 'checking'} className="px-3 py-2 text-sm font-medium text-primary-indigo border border-primary-indigo/30 rounded-lg hover:bg-indigo-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5" data-id="element-4895" data-mp="m5ry4jk8lijl">
                        {referralStatus === 'checking' ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4896" data-mp="38d661h78553p" /> : 'Apply'}
                      </button>
                    </div>
                    {referralStatus === 'valid' && <p className="flex items-center gap-1 text-xs text-green-600 mt-1.5" data-id="element-4897" data-mp="2bozwcfvg3mik">
                        <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-4898" data-mp="2pg4z3qu40ux4" /> Code
                        applied — {discountPct}% off
                      </p>}
                    {referralStatus === 'invalid' && <p className="flex items-center gap-1 text-xs text-red-500 mt-1.5" data-id="element-4899" data-mp="1wwzixi46t7pp">
                        <AlertCircleIcon className="w-3.5 h-3.5" data-id="element-4900" data-mp="1rz68udzrjtk5" /> Invalid or
                        expired code
                      </p>}
                  </div>

                  <div className="h-px bg-slate-200 my-3" data-id="element-4901" data-mp="uctzguc21w1" />
                  <div className="flex items-center justify-between mb-4" data-id="element-4902" data-mp="5mmj4unmixx9">
                    <span className="font-bold text-dark-navy" data-id="element-4903" data-mp="5oqir0ehparq">
                      Total today
                    </span>
                    <span className="font-display font-bold text-xl text-primary-indigo" data-id="element-4904" data-mp="393cttmu5hz5a">
                      AED {total.toLocaleString()}
                    </span>
                  </div>
                  <button onClick={pay} disabled={processing} className="w-full py-3 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-70" data-id="element-4905" data-mp="3j1irfs8xcj4c">
                    {processing ? <>
                        <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-4906" data-mp="12ehoxww58mdd" />{' '}
                        Processing…
                      </> : <>
                        <LockIcon className="w-4 h-4" data-id="element-4907" data-mp="2iv6xybwhoc8k" /> Pay AED{' '}
                        {total.toLocaleString()}
                      </>}
                  </button>
                  <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400 mt-3" data-id="element-4908" data-mp="2930wmbkckigp">
                    <LockIcon className="w-3 h-3" data-id="element-4909" data-mp="245hjxg3asdqv" /> Secured & encrypted
                  </p>
                </div>
              </div>
            </div>}

          {step === 'success' && <div className="text-center py-8" data-id="element-4910" data-mp="1v3rrq81ldas1">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-4911" data-mp="1q4yaq5n3j1n4">
                <CheckCircle2Icon className="w-11 h-11 text-green-600" data-id="element-4912" data-mp="2m5tz6avggj7s" />
              </motion.div>
              <h3 className="text-2xl font-display font-bold text-dark-navy mb-2" data-id="element-4913" data-mp="286knnoi72da5">
                Payment successful
              </h3>
              <p className="text-slate-600 mb-6" data-id="element-4914" data-mp="1yharnxk8cagr">
                Your{' '}
                <span className="font-bold text-dark-navy" data-id="element-4915" data-mp="1yf7turcu5d1h">{selected}</span>{' '}
                plan is now active. Enjoy the full Business Hub.
              </p>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-xl mb-8" data-id="element-4916" data-mp="1phnnx5412skj">
                <SparklesIcon className="w-4 h-4 text-primary-indigo" data-id="element-4917" data-mp="1tihe654y0jgl" />
                <span className="text-sm font-medium text-primary-indigo" data-id="element-4918" data-mp="1fhwtf3fgwdkg">
                  {selected} Plan · Active subscription
                </span>
              </div>
              <div data-id="element-4919" data-mp="10vhqj80rn5sq">
                <button onClick={onClose} className="px-8 py-3 bg-primary-indigo text-white font-bold rounded-xl hover:bg-indigo-700 transition-colors shadow-md" data-id="element-4920" data-mp="10uy87q77efe6">
                  Done
                </button>
              </div>
            </div>}
        </div>

        {/* Footer (choose step) */}
        {step === 'choose' && <div className="flex-shrink-0 px-6 py-4 border-t border-slate-100 flex justify-end gap-3" data-id="element-4921" data-mp="1jttwkkwdrzk3">
            <button onClick={onClose} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-4922" data-mp="1evsdw12va295">
              Cancel
            </button>
            <button onClick={() => setStep('pay')} className="px-6 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700" data-id="element-4923" data-mp="1okska5jp7zxd">
              Continue to payment
            </button>
          </div>}
      </motion.div>
    </div>;
}
function Field({
  label,
  placeholder
}: {
  label: string;
  placeholder: string;
}) {
  return <div data-id="element-4924" data-mp="2akpyyup8jbr4">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4925" data-mp="2amax0uardctq">
        {label}
      </label>
      <input placeholder={placeholder} className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-primary-indigo/20" data-id="element-4926" data-mp="2fjuahrzmtlkm" />
    </div>;
}